import { useState, useEffect, useCallback, memo } from "react";
import { fetchGoldData, updateGoldPrices, refetchGoldData } from "../services/scrabService.js";
import ConfirmModal from "../components/ConfirmModal.jsx";

// Utility functions
const formatRupiah = (amount) => amount?.toLocaleString("id-ID") ?? "-";

const formatDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleDateString("id-ID", {
    day: "numeric",
    month: "short",
    year: "numeric"
  });
};

const formatTime = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleTimeString("id-ID", {
    hour: "2-digit",
    minute: "2-digit"
  });
};

// Price change indicator component
const PriceChangeIndicator = memo(({ change }) => {
  const isNegative = Number(change) < 0;
  const arrow = isNegative ? '▼' : '▲';
  const colorClass = isNegative ? 'text-red-600' : 'text-green-600';

  return (
    <span className={`font-medium ${colorClass}`}>
      {arrow} {formatRupiah(Math.abs(Number(change)))}
    </span>
  );
});

// Summary card component
const SummaryCard = memo(({ summary }) => (
  <div className="bg-white p-5 rounded-lg border border-gray-200 shadow-sm">
    <h3 className="text-lg font-semibold text-gray-700 mb-3">Buy Price</h3>
    <div className="space-y-3">
      <div className="flex justify-between items-center">
        <span className="text-gray-600">Current Price:</span>
        <span className="text-xl font-bold text-gray-900">{formatRupiah(summary.harga_per_gram)}</span>
      </div>
      <div className="flex justify-between items-center">
        <span className="text-gray-600">Previous Price:</span>
        <span className="text-gray-700">{formatRupiah(summary.harga_terakhir)}</span>
      </div>
      <div className="flex justify-between items-center">
        <span className="text-gray-600">Change:</span>
        <PriceChangeIndicator change={summary.perubahan} />
      </div>
    </div>
  </div>
));

// Action buttons component
const ActionButtons = memo(({ onRefetch, onSave, isRefetching, isSaving }) => (
  <div className="flex gap-2">
    <button
      onClick={onRefetch}
      disabled={isRefetching || isSaving}
      className={`px-4 py-2 rounded-lg text-white transition-colors ${isRefetching
        ? "bg-gray-400 cursor-not-allowed"
        : "bg-green-600 hover:bg-green-700 active:bg-green-800"
        }`}
    >
      {isRefetching ? (
        <span className="flex items-center">
          <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          Refreshing...
        </span>
      ) : "Refetch Data"}
    </button>
    <button
      onClick={onSave}
      disabled={isSaving}
      className={`px-4 py-2 rounded-lg text-white transition-colors ${isSaving
        ? "bg-gray-400 cursor-not-allowed"
        : "bg-blue-600 hover:bg-blue-700 active:bg-blue-800"
        }`}
    >
      {isSaving ? (
        <span className="flex items-center">
          <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          Saving...
        </span>
      ) : "Save Updates"}
    </button>
  </div>
));


const PriceInput = memo(({ value, onChange, placeholder }) => {
  const [displayValue, setDisplayValue] = useState("");

  // Format when prop value changes (from external updates)
  useEffect(() => {
    if (value === null || value === undefined || value === "") {
      setDisplayValue("");
    } else {
      setDisplayValue(formatNumber(value));
    }
  }, [value]);

  // Helper: format number to IDR style
  const formatNumber = (num) => {
    if (num === null || num === undefined) return "";
    return Math.floor(num).toLocaleString("id-ID"); // no decimals for now
  };

  // Helper: parse formatted string to number
  const parseNumber = (str) => {
    if (!str) return null;
    const cleaned = str.replace(/\./g, "");
    const num = parseFloat(cleaned);
    return isNaN(num) ? null : num;
  };

  const handleChange = (e) => {
    const raw = e.target.value;

    // Remove all non-digit characters except .
    const cleaned = raw.replace(/[^\d]/g, "");
    const numericValue = parseNumber(cleaned);

    // Format immediately for display
    const formatted = numericValue !== null ? formatNumber(numericValue) : "";
    setDisplayValue(formatted);

    // Pass numeric value to parent
    onChange(numericValue);
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text");
    const numericValue = parseNumber(pasted);
    setDisplayValue(numericValue !== null ? formatNumber(numericValue) : "");
    onChange(numericValue);
  };

  return (
    <input
      type="text"
      value={displayValue}
      onChange={handleChange}
      onPaste={handlePaste}
      placeholder={placeholder}
      inputMode="numeric"
      className="w-32 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-right"
    />
  );
});


// Table row component
const PriceTableRow = memo(({ item, onChange }) => (
  <tr className="border-t hover:bg-gray-50 transition-colors">
    <td className="px-4 py-3 font-medium">{item.weight}</td>
    <td className="px-4 py-3">{formatRupiah(item.butiklm)}</td>
    <td className="px-4 py-3">{formatRupiah(item.kbeli)}</td>
    <td className="px-4 py-3">{formatRupiah(item.kjual)}</td>
    
    <td className="px-4 py-3">
      <PriceInput
        value={item.today_price_jual}
        onChange={(value) => onChange(item.id, "today_price_jual", value)}
        placeholder="Sell price"
      />
    </td>
    <td className="px-4 py-3">
      <PriceInput
        value={item.today_price_beli}
        onChange={(value) => onChange(item.id, "today_price_beli", value)}
        placeholder="Buy price"
      />
    </td>
  </tr>
));

// Main component
export default function GoldPriceUpdater() {
  const [data, setData] = useState(null);
  const [todayPrices, setTodayPrices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [refetching, setRefetching] = useState(false);
  const [modal, setModal] = useState({ isOpen: false, type: "", message: "", onConfirm: null });

  const fetchInitialData = useCallback(async () => {
    setLoading(true);
    try {
      const json = await fetchGoldData();
      if (!json.error) {
        setData(json);
        setTodayPrices(json.todayPrice || []);
      }
    } catch (error) {
      console.error("Error fetching gold data:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchInitialData();
  }, [fetchInitialData]);

  // Optimized handleChange to update only the specific field that changed
  const handleChange = useCallback((id, field, value) => {
    setTodayPrices(prev => {
      // Find the index of the item to update
      const index = prev.findIndex(item => item.id === id);
      if (index === -1) return prev;

      // Create a new array with the updated item
      const newPrices = [...prev];
      newPrices[index] = { ...newPrices[index], [field]: value };

      return newPrices;
    });
  }, []);

  const openModal = useCallback((type, message, onConfirm) => {
    setModal({ isOpen: true, type, message, onConfirm });
  }, []);

  const closeModal = useCallback(() => {
    setModal({ isOpen: false, type: "", message: "", onConfirm: null });
  }, []);

  const confirmSave = useCallback(async () => {
    closeModal();
    setSaving(true);

    try {
      const date = new Date().toISOString().split("T")[0];

      // Only include records that have changes in today_price_beli or today_price_jual
      const updates = todayPrices
        .filter(item => item.today_price_beli !== null || item.today_price_jual !== null)
        .map(item => ({
          weight: item.weight,
          date,
          today_price_beli: item.today_price_beli,
          today_price_jual: item.today_price_jual,
        }));

      if (updates.length === 0) {
        alert("⚠️ No changes to save.");
        return;
      }

      const result = await updateGoldPrices(updates);
      if (!result.error) {
        alert(`✅ Successfully updated ${result.updatedCount || updates.length} record(s)!`);
      } else {
        alert(`❌ Failed to update prices: ${result.error}`);
      }

      fetchInitialData();
    } catch (error) {
      console.error("Error saving prices:", error);
      alert("❌ An unexpected error occurred while saving.");
    } finally {
      setSaving(false);
    }
  }, [todayPrices, closeModal, fetchInitialData]);

  const confirmRefetch = useCallback(async () => {
    closeModal();
    setRefetching(true);

    try {
      const result = await refetchGoldData();
      if (!result.error) {
        alert("✅ Refetched successfully!");
      } else {
        alert(`❌ Failed to refetch: ${JSON.stringify(result.error)}`);
      }

      fetchInitialData();
    } catch (error) {
      console.error("Error refetching data:", error);
      alert("❌ An unexpected error occurred while refetching.");
    } finally {
      setRefetching(false);
    }
  }, [closeModal, fetchInitialData]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  const isSummary = data?.goldSummary;

  return (
    <div className="p-6 bg-white rounded-xl shadow-md">
      {/* Summary Section */}
      {isSummary && (
        <div className="mb-8 p-6 bg-gradient-to-r from-yellow-50 to-amber-50 rounded-xl border border-amber-100 shadow-sm">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-4">
            <div>
              <h2 className="text-2xl font-bold text-gray-800">Gold Price Summary</h2>
              <p className="text-gray-600 mt-1">
                {formatDate(isSummary.gold_date)} • Last updated: {formatTime(isSummary.last_updated_lm)}
              </p>
            </div>
            <div className="mt-4 md:mt-0 flex items-center">
              <span className="px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-sm font-medium">
                Live Prices
              </span>
            </div>
          </div>

          <SummaryCard summary={isSummary} />
        </div>
      )}

      {/* Header with buttons */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
        <h2 className="text-xl font-semibold text-gray-800">Update Today's Gold Prices</h2>
        <ActionButtons
          onRefetch={() => openModal("refetch", "Refetch latest data?", confirmRefetch)}
          onSave={() => openModal("save", "Save all changes?", confirmSave)}
          isRefetching={refetching}
          isSaving={saving}
        />
      </div>

      {/* Table */}
      <div className="overflow-x-auto rounded-lg border border-gray-200">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              {["Weight (gr)", "Butik LM", "K. Jual", "K. Beli", "Today Price Jual", "Today Price Beli"].map(th => (
                <th key={th} className="px-4 py-3 text-left text-sm font-medium text-gray-600 uppercase tracking-wider">{th}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {todayPrices.map(item => (
              <PriceTableRow
                key={item.id}
                item={item}
                onChange={handleChange}
              />
            ))}
          </tbody>
        </table>

        {todayPrices.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            No price data available
          </div>
        )}
      </div>

      <ConfirmModal
        isOpen={modal.isOpen}
        title={modal.type === "save" ? "Confirm Save" : "Confirm Refetch"}
        message={modal.message}
        onConfirm={modal.onConfirm}
        onCancel={closeModal}
      />
    </div>
  );
}